import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useGoBack } from '@/hooks/use-go-back'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { AppUserHelmetForm } from '@/features/app-user-helmets/components/app-user-helmet-form'
import { AppUserHelmetDuplicateBanner } from '@/features/app-user-helmets/components/app-user-helmet-duplicate-banner'
import { AppUserHelmetStolenBanner } from '@/features/app-user-helmets/components/app-user-helmet-stolen-banner'
import { useAppUserHelmetQuery } from '@/features/app-user-helmets/api/app-user-helmets.query'
import { useRequireAdmin } from '@/features/auth/hooks/use-require-admin'

export const Route = createFileRoute('/_authenticated/app-user-helmets/$appUserHelmetId/edit')({
  component: RouteComponent,
})

function formatDate(date: Date | string) {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(dateObj.getTime())) {
    return 'Data sconosciuta'
  }
  return new Intl.DateTimeFormat('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

function RouteComponent() {
  const { appUserHelmetId } = Route.useParams()
  const navigate = useNavigate()
  const goBack = useGoBack()
  const { isLoading: isAuthLoading } = useRequireAdmin('/app-user-helmets/$appUserHelmetId', { appUserHelmetId })
  const { data: appUserHelmet, isLoading } = useAppUserHelmetQuery(appUserHelmetId)

  if (isAuthLoading || isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="cursor-pointer" onClick={() => goBack({ to: '/app-user-helmets/$appUserHelmetId', params: { appUserHelmetId } })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">Modifica Casco Acquistato</h1>
        </div>
        <div className="flex h-64 items-center justify-center rounded-lg border">
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    )
  }

  if (!appUserHelmet) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="cursor-pointer" onClick={() => goBack({ to: '/app-user-helmets' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">Modifica Casco acquistato</h1>
        </div>
        <div className="flex h-64 items-center justify-center rounded-lg border">
          <p className="text-muted-foreground">Casco non trovato</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" className="cursor-pointer" onClick={() => goBack({ to: '/app-user-helmets/$appUserHelmetId', params: { appUserHelmetId } })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">Modifica Casco acquistato</h1>
      </div>

      {appUserHelmet?.duplicateOfId != null && (
        <AppUserHelmetDuplicateBanner
          currentId={appUserHelmetId}
          duplicateOfId={appUserHelmet.duplicateOfId}
          onApprove={() => navigate({ to: '/app-user-helmets/$appUserHelmetId', params: { appUserHelmetId } })}
        />
      )}

      {appUserHelmet.reportedStolenAt != null && (
        <AppUserHelmetStolenBanner
          appUserHelmetId={appUserHelmetId}
          reportedStolenAt={appUserHelmet.reportedStolenAt}
          recoveryHow={appUserHelmet.recoveryHow}
          formatDate={formatDate}
        />
      )}

      <AppUserHelmetForm appUserHelmet={appUserHelmet} />
    </div>
  )
}
